/**
 * prefetchRoutes.js
 *
 * Call these functions on onMouseEnter / onTouchStart of nav links to start
 * downloading the page chunk BEFORE the user actually navigates.
 *
 * Webpack recognises these as the same dynamic-import paths used by lazy()
 * in App.js, so both share the same chunk. Once prefetched the module is in
 * the browser's module cache — when React Suspense tries to load it on
 * navigation, the chunk is already there → instant render.
 */

// Map of pathname → prefetch function
const PREFETCH_MAP = {
    '/deals':          () => import('../pages/Deals/DealsPage'),
    '/hajj-umrah':     () => import('../pages/HajjUmrah/HajjUmrahPage'),
    '/flights':        () => import('../pages/Flights/FlightSearch'),
    '/tours':          () => import('../pages/Tours/ToursPage'),
    '/hotels':         () => import('../pages/Hotels/Hotels'),
    '/destinations':   () => import('../pages/Destinations/Destinations'),
    '/blog':           () => import('../pages/Blog/Blog'),
    '/visa':           () => import('../pages/Visa/VisaPage'),
    '/hotels-partner': () => import('../pages/OfflineHotels/OfflineHotelsPage'),
    '/contact':        () => import('../pages/Contact/ContactPage'),
    '/about':          () => import('../pages/About/About'),
    '/experiences':    () => import('../pages/Experiences/Experiences'),
    '/support':        () => import('../pages/Support/SupportPage'),
    '/faq':            () => import('../pages/FAQ/FAQPage'),
};

/**
 * Trigger the prefetch for a given pathname.
 * Safe to call multiple times — the browser/webpack caches the module.
 * @param {string} pathname  e.g. '/hajj-umrah'
 */
export function prefetchRoute(pathname) {
    const fn = PREFETCH_MAP[pathname];
    if (!fn) return;
    fn().catch((err) => {
        // Prefetch is only an optimisation. In dev or after a rebuild, chunks can
        // disappear briefly; never let that crash the page.
        if (process.env.NODE_ENV === 'development') {
            console.debug('Route prefetch skipped:', pathname, err?.message || err);
        }
    });
}

/**
 * Returns an event handler that extracts the target link's pathname
 * and prefetches its chunk. Use via event delegation on a parent element
 * so you don't need to add handlers to every individual <Link>.
 *
 * Usage:
 *   <nav onMouseOver={routePrefetchHandler} onTouchStart={routePrefetchHandler}>
 */
export function routePrefetchHandler(e) {
    // Walk up from the event target to find the nearest <a> with an href
    const anchor = e.target.closest('a[href]');
    if (!anchor) return;
    try {
        const pathname = new URL(anchor.href, window.location.origin).pathname;
        prefetchRoute(pathname);
    } catch {
        // Ignore malformed hrefs
    }
}
